/**
 * Lead Production Command Center screen.
 */
import { formatCurrency, formatInteger } from "./format.js";
import {
  escapeAttr,
  escapeHtml,
  labelize,
  metric,
  pill
} from "./dashboard-html.js";
import type {
  LeadFactoryBundle,
  LeadFactoryContactWorkSummary,
  LeadFactoryLead,
  LeadFactoryLeadSupplyBlockersResponse,
  LeadFactoryTask,
  LeadGenerationWorkbenchLead,
  MortgageEvidenceReviewLead
} from "./dashboard-lead-factory-types.js";
import type { EcourtsHealth } from "../../../packages/core/src/ecourts-performance.js";

export function isReturnedMailRetryLetterTask(task: LeadFactoryTask): boolean {
  return task.task_type === "prepare_letter" && task.blocking_reason === "returned_mail_retry_letter";
}


export type TreesPipelineCounts = {
  properties: number;
  waiting?: number;
  latestScrapeAt: string | null;
  seenOnLatestScrape: number;
  sources: Array<{ source: string; properties: number; lastSeenAt: string | null }>;
  stages: Array<{
    id: string;
    label: string;
    count: number;
    ready?: number;
    sick?: string | null;
    running?: boolean;
    deadends?: Array<{ class: string; count: number }>;
    auctionTiming?: TreesPipelineAuctionTimingCounts;
    work?: {
      eligible: number;
      executing: number | null;
      scheduledWait: number;
      providerWait: number;
      rejected: number;
      throughput: number | null;
      oldestEligibleAt: string | null;
      lastVerifiedSuccessAt: string | null;
      nextRetryAt: string | null;
      nextWakeupAt: string | null;
    };
  }>;
  auctionTiming?: TreesPipelineAuctionTimingCounts;
  blocker: string | null;
  failures: Array<{ class: string; count: number }>;
};

export type TreesPipelineAuctionTimingCounts = {
  within_30_days: number;
  over_30_days: number;
  past_or_unknown: number;
};

export type PropertiesConveyorHealth = {
  generatedAt?: string;
  ecourts?: EcourtsHealth;
  rows: number;
  awaitingSkipTrace: number;
  needsOwner: number;
  withAvm: number;
  withPlv: number;
  withEquity: number;
  deceasedYes: number;
  withClose: number;
  readyToCall: number;
  onHold: number;
  gatedBlock: number;
  blocker: string | null;
  persistError?: string | null;
  snapshotStatus?: "live" | "cached";
  tree?: TreesPipelineCounts;
  ingest?: {
    enabled?: boolean;
    lastTickAt?: string | null;
    lastOk?: boolean | null;
    lastFailureClass?: string | null;
    lastAttemptAt?: string | null;
    sources?: Array<{
      source: string;
      snapshotId?: string | null;
      lastAttemptAt?: string | null;
      fetched?: number | null;
      accepted?: number | null;
      remaining?: number | null;
      completed?: boolean | null;
      error?: string | null;
    }>;
  };
  tools?: Array<{
    toolId: string;
    title: string;
    state: string;
    failureClass: string | null;
    message: string | null;
    attempted: number | null;
    houseMisses: number;
  }>;
  workflowFailureClass?: string | null;
  scan?: {
    rows: number;
    artifactBytes: number;
    compactBytes: number;
    durationMs: number;
  };
};

export function sevenTreesWorkflowHtml(health: PropertiesConveyorHealth | null = null): string {
  const state = health ? "live" : "unavailable";
  const skipLabel = health
    ? `Awaiting SmartSkip · ${formatInteger(health.awaitingSkipTrace)}`
    : "Need skip · unavailable";
  const valuedLabel = health
    ? `AVM ${formatInteger(health.withAvm)} · EPLV ${formatInteger(health.withPlv)} · equity ${formatInteger(health.withEquity)}`
    : "Deceased Yes awaiting AVM+EPLV · unavailable";
  const gateLabel = health
    ? `Deceased ${formatInteger(health.deceasedYes)} · gated block ${formatInteger(health.gatedBlock)}`
    : "Equity / obit ($25k) · unavailable";
  const closeLabel = health
    ? `Close ${formatInteger(health.withClose)} · Ready ${formatInteger(health.readyToCall)} · On Hold ${formatInteger(health.onHold)}`
    : "Ready for Close · unavailable";
  const note = health
    ? `Live D1 house: ${formatInteger(health.rows)} properties. Blocker: ${health.blocker ?? "none"}. SmartSkip has no API; leftover Close skip rows copy automatically.`
    : "Tree/gate counts are not on this Lead Factory fetch yet. Missing counts are not “clear.” 0 is not a real AVM or EPLV. Unfunded SmartSkip / ATTOM / Bright Data / Exa must alert, not silent park.";
  return `
    <section id="seven-trees" class="seven-trees-workflow" data-seven-trees-workflow>
      <div class="panel-heading">
        <div>
          <h3>Seven trees</h3>
          <p>Ingest → identity → ReportAll → SmartSkip → AVM+EPLV → equity → obit → Close Ready/On Hold.</p>
        </div>
      </div>
      <p class="subtle" data-seven-trees-names>Logs (upcoming + held) · Brock and Scott · Hutchens · Ingle · Bell Carrington · MTG · Catawba. Kania is not a tree.</p>
      <div class="seven-trees-stages" data-seven-trees-stages data-state="${state}">
        <span class="pill ${health ? "online" : "unavailable"}">${escapeHtml(skipLabel)}</span>
        <span class="pill ${health ? "online" : "unavailable"}">${escapeHtml(valuedLabel)}</span>
        <span class="pill ${health ? "online" : "unavailable"}">${escapeHtml(gateLabel)}</span>
        <span class="pill ${health ? "online" : "unavailable"}">${escapeHtml(closeLabel)}</span>
      </div>
      <small>${escapeHtml(note)}</small>
      <p class="subtle"><a href="/trees-pipeline">Open trees pipeline overview</a></p>
    </section>
  `;
}

export function leadProductionCommandCenterHtml(bundle: LeadFactoryBundle, compact = false): string {
  const {
    businessMetrics,
    leadGenerationWorkbench,
    mortgageEvidenceReview,
    leadGenerationQueue,
    leadSupplyBlockers,
    contactQueue,
    contactWorkQueue,
    reviewQueue
  } = bundle;
  const readyLeadCandidates = contactQueue.leads.length > 0
    ? contactQueue.leads
    : leadGenerationWorkbench.leads.filter((lead) => lead.contact_readiness.startsWith("ready_"));
  const visibleReadyLeads = readyLeadCandidates.slice(0, 6);
  const visibleMortgageBlockers = mortgageEvidenceReview.leads
    .filter((lead) => lead.review_state !== "verified_amount_captured" && lead.review_state !== "terminal_no_source_evidence")
    .slice(0, 6);
  const contactSummary = contactWorkQueue.summary;
  const assignedQueueCount = leadGenerationQueue.summary.assigned + leadGenerationQueue.summary.unassigned;
  const sourceBackedFacts = mortgageEvidenceReview.summary.sourceBackedVerifiedFacts ?? mortgageEvidenceReview.summary.verifiedFacts;
  const terminalOutcomes = mortgageEvidenceReview.summary.terminalDispositions ?? 0;
  const missingMortgageEvidence = mortgageEvidenceReview.summary.blockedByMissingEvidence;
  const activeLeadSupplyBlockers = leadSupplyBlockers.summary.activeBlockerTasks;
  return `
    <section id="lead-production-command-center" class="lead-production-command-center" data-lead-production-command-center>
      <div class="panel-heading">
        <div>
          <h2>Lead Production Command Center</h2>
          <p class="subtle">Ready leads are usable now; mortgage evidence gaps stay in their own operator queue.</p>
        </div>
        <span class="live-badge online">lead production</span>
      </div>
      ${leadProductionOperatorBriefHtml(bundle, visibleReadyLeads.length, missingMortgageEvidence, activeLeadSupplyBlockers)}
      ${compact ? "" : `<div class="lead-production-metrics">
        ${metric("Ready Leads Now", formatInteger(businessMetrics.leads.readyToContact), "usable owner-contact inventory", businessMetrics.leads.readyToContact > 0 ? "pass" : "not_ready")}
        ${metric("Ready Queue Loaded", formatInteger(visibleReadyLeads.length), "first-screen leads with actions", visibleReadyLeads.length > 0 ? "pass" : "not_ready")}
        ${metric("Mortgage Blockers", formatInteger(missingMortgageEvidence), "separate evidence queue", missingMortgageEvidence > 0 ? "not_ready" : "pass")}
        ${metric("Source-Backed Facts", formatInteger(sourceBackedFacts), "real mortgage evidence only", sourceBackedFacts > 0 ? "pass" : "not_ready")}
        ${metric("Terminal No-Source", formatInteger(terminalOutcomes), "typed outcomes, no fake values", terminalOutcomes > 0 ? "pass" : "not_ready")}
        ${metric("Contact Work Ready", formatInteger(contactSummary?.ready ?? contactWorkQueue.tasks.length), "call, letter, PI, skip-trace tasks", (contactSummary?.ready ?? contactWorkQueue.tasks.length) > 0 ? "pass" : "not_ready")}
      </div>`}
      ${sevenTreesWorkflowHtml()}
      ${leadProductionQueueSwitchboardHtml(bundle)}
      ${compact ? "" : `<div class="lead-production-lanes">
        <section class="lead-production-lane" data-lead-production-ready-lane>
          <div class="lead-production-lane-heading">
            <div>
              <h3>Useful Leads Now</h3>
              <p>${formatInteger(businessMetrics.leads.readyToContact)} ready-to-contact leads · ${formatInteger(assignedQueueCount)} lead-generation queue rows</p>
            </div>
            ${pill("ready", "pass")}
          </div>
          <div class="lead-production-list">
            ${visibleReadyLeads.map(leadProductionReadyLeadHtml).join("") || `<p class="empty">No ready lead rows loaded on this view.</p>`}
          </div>
        </section>
        <section class="lead-production-lane" data-lead-production-mortgage-blocker-lane>
          <div class="lead-production-lane-heading">
            <div>
              <h3>Mortgage Evidence Blockers</h3>
              <p>${formatInteger(missingMortgageEvidence)} blocked by missing evidence · ${formatInteger(sourceBackedFacts)} source-backed · ${formatInteger(terminalOutcomes)} terminal</p>
            </div>
            ${pill(missingMortgageEvidence > 0 ? "blocked" : "clear", missingMortgageEvidence > 0 ? "not_ready" : "pass")}
          </div>
          <div class="lead-production-fact-state">
            <span data-lead-production-real-facts>${formatInteger(sourceBackedFacts)} source-backed mortgage facts</span>
            <span data-lead-production-missing-facts>${formatInteger(missingMortgageEvidence)} missing mortgage evidence rows</span>
            <span data-lead-production-terminal-outcomes>${formatInteger(terminalOutcomes)} terminal no-source outcomes</span>
          </div>
          <div class="lead-production-list">
            ${visibleMortgageBlockers.map(leadProductionMortgageBlockerHtml).join("") || `<p class="empty">No mortgage evidence blockers loaded.</p>`}
          </div>
        </section>
        <section class="lead-production-lane" data-lead-production-contact-work-lane>
          <div class="lead-production-lane-heading">
            <div>
              <h3>Operator Work Queues</h3>
              <p>${formatInteger(contactSummary?.loaded ?? contactWorkQueue.tasks.length)} contact work rows · ${formatInteger(activeLeadSupplyBlockers)} lead-supply blockers</p>
            </div>
            ${pill("workable", "pass")}
          </div>
          ${leadProductionContactWorkSummaryHtml(contactSummary, contactWorkQueue.tasks)}
          ${leadProductionSupplyPressureHtml(leadSupplyBlockers)}
        </section>
      </div>`}
    </section>
  `;
}

export function leadProductionOperatorBriefHtml(
  bundle: LeadFactoryBundle,
  loadedReadyLeads: number,
  missingMortgageEvidence: number,
  activeLeadSupplyBlockers: number
): string {
  const { businessMetrics, leadSupplyBlockers, reviewQueue, contactWorkQueue } = bundle;
  const contactSummary = contactWorkQueue.summary;
  const reviewAttention = reviewQueue.summary.blocked + reviewQueue.summary.overdue;
  const contactAttention = (contactSummary?.unassigned ?? 0) + (contactSummary?.overdue ?? 0);
  const topSourceBlocker = leadSupplyBlockers.blockers[0];
  const next =
    missingMortgageEvidence > 0
      ? {
          label: "Mortgage evidence",
          href: "#lead-blockers",
          count: missingMortgageEvidence,
          note: "clear missing source-backed amounts or record typed no-source outcomes"
        }
      : activeLeadSupplyBlockers > 0
        ? {
            label: "Source supply",
            href: "#source-supply",
            count: activeLeadSupplyBlockers,
            note: topSourceBlocker ? topSourceBlocker.nextAction : "work approved source-supply lanes"
          }
        : reviewAttention > 0
          ? {
              label: "Review queue",
              href: "#review-queue",
              count: reviewAttention,
              note: "resolve blocked or overdue source-document review work"
            }
          : contactAttention > 0
            ? {
                label: "Contact prep",
                href: "#contact-prep",
                count: contactAttention,
                note: "assign or clear overdue owner-contact work"
              }
            : {
                label: "Ready leads",
                href: "#ready-leads",
                count: businessMetrics.leads.readyToContact,
                note: "work usable leads now"
              };
  return `
    <section class="lead-production-operator-brief" data-lead-production-operator-brief>
      <article>
        <span>Useful leads now</span>
        <strong>${formatInteger(businessMetrics.leads.readyToContact)}</strong>
        <small>${formatInteger(loadedReadyLeads)} loaded with actions on this screen</small>
      </article>
      <article>
        <span>Current bottleneck</span>
        <strong>${escapeHtml(next.label)}</strong>
        <small>${formatInteger(next.count)} row${next.count === 1 ? "" : "s"} · ${escapeHtml(next.note)}</small>
      </article>
      <article>
        <span>Source pressure</span>
        <strong>${formatInteger(activeLeadSupplyBlockers)}</strong>
        <small>${topSourceBlocker ? escapeHtml(`${topSourceBlocker.label}: ${topSourceBlocker.nextAction}`) : "No source-supply blocker loaded"}</small>
      </article>
      <a class="button primary" href="${escapeAttr(next.href)}" data-lead-production-primary-action>Work ${escapeHtml(next.label)}</a>
    </section>
  `;
}

export function leadProductionQueueSwitchboardHtml(bundle: LeadFactoryBundle): string {
  const {
    businessMetrics,
    leadGenerationWorkbench,
    mortgageEvidenceReview,
    leadSupplyBlockers,
    reviewQueue,
    contactQueue,
    contactWorkQueue
  } = bundle;
  const contactSummary = contactWorkQueue.summary;
  const readyLoaded = contactQueue.leads.length || leadGenerationWorkbench.summary.ready;
  const missingMortgageEvidence = mortgageEvidenceReview.summary.blockedByMissingEvidence;
  const sourceSupplyTopBlocker = leadSupplyBlockers.blockers[0];
  const rows = [
    {
      queue: "Ready Leads",
      href: "#ready-leads",
      count: businessMetrics.leads.readyToContact,
      loaded: readyLoaded,
      status: businessMetrics.leads.readyToContact > 0 ? "workable" : "empty",
      statusLabel: businessMetrics.leads.readyToContact > 0 ? "ready to work" : "empty",
      nextAction: readyLoaded > 0 ? "Open loaded ready leads and prepare contact." : "Load or route ready contact inventory."
    },
    {
      queue: "Lead Blockers",
      href: "#lead-blockers",
      count: missingMortgageEvidence,
      loaded: mortgageEvidenceReview.leads.length,
      status: missingMortgageEvidence > 0 ? "blocked" : "clear",
      statusLabel: missingMortgageEvidence > 0 ? "needs evidence" : "clear",
      nextAction: missingMortgageEvidence > 0 ? "Cannot advance until mortgage evidence is source-backed or terminal no-source is recorded." : "No active mortgage evidence blocker loaded."
    },
    {
      queue: "Source Supply",
      href: "#source-supply",
      count: leadSupplyBlockers.summary.activeBlockerTasks,
      loaded: leadSupplyBlockers.blockers.length,
      status: leadSupplyBlockers.summary.activeBlockerTasks > 0 ? "blocked" : "clear",
      statusLabel: leadSupplyBlockers.summary.activeBlockerTasks > 0 ? "needs source" : "clear",
      nextAction: sourceSupplyTopBlocker
        ? `${sourceSupplyTopBlocker.label}: ${sourceSupplyTopBlocker.nextAction}`
        : "No source-supply blocker loaded."
    },
    {
      queue: "Review Queue",
      href: "#review-queue",
      count: reviewQueue.summary.total,
      loaded: reviewQueue.tasks.length,
      status: reviewQueue.summary.blocked > 0 || reviewQueue.summary.overdue > 0 ? "attention" : reviewQueue.summary.ready > 0 ? "workable" : "clear",
      statusLabel: reviewQueue.summary.blocked > 0 || reviewQueue.summary.overdue > 0 ? "needs review" : reviewQueue.summary.ready > 0 ? "ready to work" : "clear",
      nextAction: `${formatInteger(reviewQueue.summary.blocked)} blocked · ${formatInteger(reviewQueue.summary.overdue)} overdue · ${formatInteger(reviewQueue.summary.ready)} ready.`
    },
    {
      queue: "Contact Prep",
      href: "#contact-prep",
      count: contactSummary?.ready ?? contactWorkQueue.tasks.length,
      loaded: contactWorkQueue.tasks.length,
      status: (contactSummary?.unassigned ?? 0) > 0 || (contactSummary?.overdue ?? 0) > 0 ? "attention" : (contactSummary?.ready ?? contactWorkQueue.tasks.length) > 0 ? "workable" : "clear",
      statusLabel: (contactSummary?.unassigned ?? 0) > 0 || (contactSummary?.overdue ?? 0) > 0 ? "needs assignment" : (contactSummary?.ready ?? contactWorkQueue.tasks.length) > 0 ? "ready to work" : "clear",
      nextAction: `${formatInteger(contactSummary?.unassigned ?? 0)} unassigned · ${formatInteger(contactSummary?.overdue ?? 0)} overdue · ${formatInteger((contactSummary?.standardLetters ?? 0) + (contactSummary?.retryLetters ?? 0))} letters.`
    }
  ];
  const nextQueue =
    rows.find((row) => row.status === "blocked" || row.status === "attention") ??
    rows.find((row) => row.status === "workable") ??
    rows[0] ?? {
      queue: "Command Center",
      href: "#lead-production-command-center",
      count: 0,
      loaded: 0,
      status: "clear",
      statusLabel: "clear",
      nextAction: "No lead-production queue rows loaded."
    };
  return `
    <section class="lead-queue-switchboard" data-lead-queue-switchboard aria-label="lead production queue switchboard">
      <div class="lead-queue-switchboard-header">
        <div>
          <h3>Next Queue To Work</h3>
          <p>${escapeHtml(nextQueue.queue)} · ${escapeHtml(nextQueue.nextAction)}</p>
          <small>Needs evidence/source/review means the row cannot advance until an operator resolves that missing proof or task state.</small>
        </div>
        <a class="button primary" href="${escapeAttr(nextQueue.href)}">Open ${escapeHtml(nextQueue.queue)}</a>
      </div>
      <div class="lead-queue-switchboard-table">
        ${rows.map(leadProductionQueueSwitchboardRowHtml).join("")}
      </div>
    </section>
  `;
}

export function leadProductionQueueSwitchboardRowHtml(row: {
  queue: string;
  href: string;
  count: number;
  loaded: number;
  status: string;
  statusLabel: string;
  nextAction: string;
}): string {
  return `
    <a class="lead-queue-switchboard-row ${escapeAttr(row.status)}" href="${escapeAttr(row.href)}" data-lead-queue-switchboard-row>
      <span>
        <strong>${escapeHtml(row.queue)}</strong>
        <small>${escapeHtml(row.nextAction)}</small>
      </span>
      <em>${formatInteger(row.count)}</em>
      <small>${formatInteger(row.loaded)} loaded</small>
      ${pill(row.statusLabel, row.status)}
    </a>
  `;
}

export function leadProductionReadyLeadHtml(lead: LeadFactoryLead | LeadGenerationWorkbenchLead): string {
  const title = lead.property_address || lead.owner_name || lead.id;
  const sourceDocumentId = "primary_source_document_id" in lead ? lead.primary_source_document_id || "" : "";
  return `
    <article class="lead-production-row" data-lead-production-ready-row data-lead-id="${escapeAttr(lead.id)}">
      <div>
        <strong>${escapeHtml(title)}</strong>
        <small>${escapeHtml(lead.county)} · ${escapeHtml(labelize(lead.contact_readiness))} · ${escapeHtml(labelize(lead.priority || "unscored"))}</small>
        ${"primary_source_document_summary" in lead && lead.primary_source_document_summary ? `<p data-lead-production-source-provenance>${escapeHtml(lead.primary_source_document_summary)}</p>` : ""}
      </div>
      <div class="inline-actions">
        <button class="button secondary" type="button" data-lead-action="detail" data-lead-id="${escapeAttr(lead.id)}">Detail</button>
        ${sourceDocumentId ? `<button class="button secondary" type="button" data-source-document-review="${escapeAttr(sourceDocumentId)}">Review PDF</button>` : ""}
      </div>
    </article>
  `;
}

export function leadProductionMortgageBlockerHtml(lead: MortgageEvidenceReviewLead): string {
  const title = lead.property_address || lead.owner_name || lead.id;
  const sourceDocumentId = lead.primary_source_document_id || "";
  const amountLabel =
    lead.mortgage_evidence_status === "source_backed" && typeof lead.mortgage_amount === "number"
      ? formatCurrency(lead.mortgage_amount)
      : "Amount not source-backed";
  return `
    <article class="lead-production-row blocked" data-lead-production-mortgage-blocker-row data-lead-id="${escapeAttr(lead.id)}">
      <div>
        <strong>${escapeHtml(title)}</strong>
        <small>${escapeHtml(lead.county)} · ${escapeHtml(labelize(lead.review_state))}</small>
        <p data-lead-production-source-provenance>${escapeHtml(lead.source_document_summary || lead.primary_source_document_summary || "source document pending")}</p>
      </div>
      <div class="lead-production-row-state">
        ${pill(amountLabel, lead.mortgage_evidence_status === "source_backed" ? "pass" : "not_ready")}
        <button class="button secondary" type="button" data-lead-action="detail" data-lead-id="${escapeAttr(lead.id)}">Lead</button>
        ${sourceDocumentId ? `<button class="button secondary" type="button" data-source-document-review="${escapeAttr(sourceDocumentId)}">Review PDF</button>` : ""}
      </div>
    </article>
  `;
}

export function leadProductionContactWorkSummaryHtml(summary: LeadFactoryContactWorkSummary | undefined, tasks: LeadFactoryTask[]): string {
  const fallback = {
    calls: tasks.filter((task) => task.task_type === "call_owner").length,
    standardLetters: tasks.filter((task) => task.task_type === "prepare_letter" && !isReturnedMailRetryLetterTask(task)).length,
    retryLetters: tasks.filter(isReturnedMailRetryLetterTask).length,
    skipTrace: tasks.filter((task) => task.task_type === "skip_trace").length,
    pi: tasks.filter((task) => task.task_type === "pi_research").length
  };
  return `
    <div class="lead-production-queue-counts">
      <span>Calls <strong>${formatInteger(summary?.calls ?? fallback.calls)}</strong></span>
      <span>Letters <strong>${formatInteger(summary?.standardLetters ?? fallback.standardLetters)}</strong></span>
      <span>Retry Letters <strong>${formatInteger(summary?.retryLetters ?? fallback.retryLetters)}</strong></span>
      <span>Skip Trace <strong>${formatInteger(summary?.skipTrace ?? fallback.skipTrace)}</strong></span>
      <span>PI <strong>${formatInteger(summary?.pi ?? fallback.pi)}</strong></span>
    </div>
  `;
}

export function leadProductionSupplyPressureHtml(blockers: LeadFactoryLeadSupplyBlockersResponse): string {
  const topBlocker = blockers.blockers[0];
  return `
    <div class="lead-production-supply-pressure">
      <strong>Lead supply volume</strong>
      <p>${formatInteger(blockers.summary.activeLeadSupplyLeads)} active supply leads · ${formatInteger(blockers.summary.activeBlockerTasks)} blocker tasks.</p>
      ${
        topBlocker
          ? `<small>Top blocker: ${escapeHtml(topBlocker.label)} · ${formatInteger(topBlocker.leadCount)} leads · ${escapeHtml(topBlocker.nextAction)}</small>`
          : `<small>No lead-supply blocker row loaded.</small>`
      }
    </div>
  `;
}
